<template>
  <span v-if="content.hasContent" class="field-help">
    <v-menu
      v-model="open"
      offset-y
      :nudge-bottom="6"
      :min-width="0"
      :max-width="360"
      :close-on-content-click="false"
      disable-keys
      content-class="field-help-menu"
    >
      <template v-slot:activator="{ attrs }">
        <button
          ref="trigger"
          v-bind="attrs"
          type="button"
          class="field-help__trigger"
          :aria-label="label ? `Aide pour ${label}` : 'Aide du champ'"
          aria-haspopup="dialog"
          :aria-expanded="String(open)"
          :aria-controls="panelId"
          @mousedown.stop.prevent
          @click.stop.prevent="toggle"
          @keydown.stop="onKeydown"
        ><span class="mdi mdi-help-circle-outline" aria-hidden="true"></span></button>
      </template>
      <v-card
        :id="panelId"
        ref="panel"
        class="field-help__panel"
        role="dialog"
        :aria-labelledby="titleId"
        tabindex="-1"
        @click.stop
        @mousedown.stop
        @keydown.stop="onKeydown"
      >
        <div class="field-help__header">
          <div :id="titleId" class="field-help__title">{{ label || 'Aide du champ' }}</div>
          <ChatShareMenu v-if="fieldReference" :reference="fieldReference" :modal-key="modalKey" :hover-only="false" label="Discuter de ce champ" @shared="close()" />
        </div>
        <FieldHelpContent :help="content" />
      </v-card>
    </v-menu>
  </span>
</template>

<script>
import { normalizeFieldHelp } from './helpContent'
import FieldHelpContent from './FieldHelpContent.vue'
import ChatShareMenu from '@/components/chat/ChatShareMenu.vue'

let helpSequence = 0

export default {
  name: 'FieldHelp',
  components: { FieldHelpContent, ChatShareMenu },
  props: {
    help: { type: Object, default: null },
    label: { type: String, default: '' },
    fieldId: { type: [Number, String], default: null },
    modalKey: { type: String, default: '' },
  },
  data() {
    const id = ++helpSequence
    return { open: false, panelId: `field-help-${id}`, titleId: `field-help-title-${id}` }
  },
  computed: {
    content() { return normalizeFieldHelp(this.help) },
    fieldReference() {
      const id = Number(this.fieldId)
      return Number.isSafeInteger(id) && id > 0 ? { type: 'field', context: { field_id: id } } : null
    },
  },
  watch: {
    open(value) {
      if (value) this.$nextTick(() => this.focusPanel())
    },
    'content.hasContent'(value) { if (!value) this.open = false },
  },
  methods: {
    focusPanel(attempts = 10) {
      // VMenu becomes visible after two animation frames, later than nextTick.
      if (!this.open || this._isDestroyed || !this.$refs.panel) return
      const element = this.$refs.panel.$el || this.$refs.panel
      if (element.getClientRects().length) this.focus(element)
      else if (attempts > 0) requestAnimationFrame(() => this.focusPanel(attempts - 1))
    },
    focus(element) {
      if (element && typeof element.focus === 'function') element.focus({ preventScroll: true })
    },
    toggle() {
      if (this.open) this.close(true)
      else this.open = true
    },
    close(restoreFocus = false) {
      this.open = false
      if (restoreFocus) this.$nextTick(() => this.focus(this.$refs.trigger))
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.open) {
        event.preventDefault()
        this.close(true)
      }
    },
  },
}
</script>

<style scoped>
.field-help {
  display: inline-flex;
  vertical-align: middle;
  margin-left: 4px;
  pointer-events: auto;
}
.field-help__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #737b89;
  opacity: .75;
  cursor: pointer;
  pointer-events: auto;
  line-height: 1;
}
.field-help__trigger .mdi { font-size: 14px; }
.theme--dark .field-help__trigger { color: #b6bec9; }
.field-help__trigger:hover,
.field-help__trigger:focus-visible,
.field-help__trigger[aria-expanded="true"] {
  color: var(--app-primary, #4f56a5);
  opacity: 1;
}
.field-help__trigger:focus-visible { outline: 2px solid currentColor; outline-offset: 1px; }
.field-help__panel {
  width: 360px;
  max-width: calc(100vw - 24px);
  max-height: min(360px, calc(100vh - 48px));
  overflow: auto;
  padding: 16px;
  font-size: 13px;
  line-height: 1.55;
  white-space: normal;
  overflow-wrap: anywhere;
}
.field-help__panel:focus { outline: none; }
.field-help__header { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
.field-help__title { min-width: 0; font-size: 14px; font-weight: 600; }
</style>
